import React from "react";
import { NavLink, Outlet, useNavigate, Navigate } from "react-router-dom";
import { LayoutDashboard, FileText, User, LogOut, Menu, X, ScrollText } from "lucide-react";
import { useCustomerAuth } from "./CustomerAuthContext";

const navItems = [
  { to: "/customer", end: true, label: "Dashboard", icon: LayoutDashboard },
  { to: "/customer/sales-documents", label: "Sales Documents", icon: FileText },
  { to: "/customer/statement", label: "Account Statement", icon: ScrollText },
  { to: "/customer/profile", label: "Profile", icon: User },
];

export default function CustomerLayout() {
  const { customer, loading, logout } = useCustomerAuth();
  const nav = useNavigate();
  const [open, setOpen] = React.useState(false);

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center text-slate-400">Loading…</div>;
  }
  if (!customer) return <Navigate to="/customer/login" replace />;

  const handleLogout = () => { logout(); nav("/customer/login", { replace: true }); };

  return (
    <div className="min-h-screen bg-slate-50 flex" data-testid="customer-portal">
      <aside className="hidden md:flex md:w-64 flex-col border-r border-slate-200 bg-white">
        <div className="h-16 flex items-center px-6 border-b border-slate-100">
          <div>
            <div className="font-black text-[#0a2a5e] tracking-tight">BAISHALII</div>
            <div className="text-[10px] uppercase tracking-wider text-slate-400 -mt-0.5">Customer Portal</div>
          </div>
        </div>
        <nav className="flex-1 py-4 px-3 space-y-1">
          {navItems.map(({ to, end, label, icon: Icon }) => (
            <NavLink
              key={to} to={to} end={end}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  isActive ? "bg-[#0a2a5e] text-white" : "text-slate-600 hover:bg-slate-100"
                }`
              }
            >
              <Icon className="h-4 w-4" />
              {label}
            </NavLink>
          ))}
        </nav>
        <button
          onClick={handleLogout}
          data-testid="customer-logout"
          className="m-3 flex items-center gap-2 px-3 py-2 text-sm font-medium text-slate-600 hover:bg-red-50 hover:text-red-600 rounded-lg"
        >
          <LogOut className="h-4 w-4" /> Sign out
        </button>
      </aside>

      <div className="md:hidden fixed top-0 inset-x-0 h-14 bg-white border-b border-slate-200 flex items-center px-4 z-30">
        <button onClick={() => setOpen(true)} className="p-2 -ml-2" data-testid="customer-menu-btn"><Menu className="h-5 w-5" /></button>
        <div className="ml-2 font-black text-[#0a2a5e]">BAISHALII Customer</div>
      </div>

      {open && (
        <div className="md:hidden fixed inset-0 z-40 bg-black/40" onClick={() => setOpen(false)}>
          <div onClick={(e) => e.stopPropagation()} className="w-64 h-full bg-white flex flex-col">
            <div className="h-14 flex items-center justify-between px-4 border-b">
              <div className="font-black text-[#0a2a5e]">Menu</div>
              <button onClick={() => setOpen(false)} className="p-1"><X className="h-5 w-5" /></button>
            </div>
            <nav className="flex-1 p-3 space-y-1">
              {navItems.map(({ to, end, label, icon: Icon }) => (
                <NavLink
                  key={to} to={to} end={end}
                  onClick={() => setOpen(false)}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium ${
                      isActive ? "bg-[#0a2a5e] text-white" : "text-slate-600 hover:bg-slate-100"
                    }`
                  }
                >
                  <Icon className="h-4 w-4" />
                  {label}
                </NavLink>
              ))}
            </nav>
            <button onClick={() => { handleLogout(); setOpen(false); }} className="m-3 flex items-center gap-2 px-3 py-2 text-sm text-slate-600 hover:bg-red-50 hover:text-red-600 rounded-lg">
              <LogOut className="h-4 w-4" /> Sign out
            </button>
          </div>
        </div>
      )}

      <main className="flex-1 pt-14 md:pt-0 min-h-screen">
        <div className="hidden md:flex h-16 items-center justify-end px-8 border-b border-slate-200 bg-white">
          <div className="text-sm">
            <div className="font-semibold text-slate-800">{customer.name}</div>
            <div className="text-xs text-slate-500">{customer.email}</div>
          </div>
        </div>
        <div className="p-4 md:p-8">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
